import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { IconInfoCircle } from '@tabler/icons-react';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { BalanceSkeleton } from '../SkeletonLoader';
import { interpolate } from '../../utils/entryType';
import { summarizeBudgetsByCategory, dashboardCategoryTable } from '../../utils/budgetsByCategory';
import FigureNote from './FigureNote';

// Board columns (#2207): Category · count | Spent | Still available | Budgets over.
const COLUMNS = 'minmax(0, 1fr) 112px 124px 150px';

// #2167: a category name is an in-page link to that category's Budgets group
// further down the dashboard (DashboardBudgetGroups marks each group with
// data-dashboard-budget-group). A collapsed group opens first, so the scroll
// never lands on a closed header. The app bar is sticky, so the target sits
// its rendered height (44px on md+, two rows below md) plus a small gap
// below the top of the viewport; scrollIntoView would tuck it underneath.
const SCROLL_GAP_PX = 8;
const scrollToBudgetGroup = (key) => {
  const group = document.querySelector(`[data-dashboard-budget-group="${key}"]`);
  if (!group) return;
  const closedHeader = group.querySelector(':scope > button[aria-expanded="false"]');
  if (closedHeader) closedHeader.click();
  const bar = document.querySelector('header.MuiAppBar-root');
  const barHeight = bar ? bar.getBoundingClientRect().height : 0;
  window.scrollTo({ top: group.getBoundingClientRect().top + window.scrollY - barHeight - SCROLL_GAP_PX, behavior: 'smooth' });
};

// "Oct '26" in the given locale: short month, then an apostrophe and the
// two-digit year, so the title reads as the board has it.
const shortMonthYear = (yearMonth, locale) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const parts = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).formatToParts(new Date(y, m - 1, 1));
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  return `${month} '${year}`;
};

const joinNames = (names) => names.join(', ');

/**
 * Month to month budgets (#2143, #2179, #2207, epics #2138 and #2201): the
 * current month's Month to month Normal budgets summed per category, then a
 * Total row over those categories. Planning ahead budgets are not on the
 * table. Spent comes from `/budget-months`, Still available from `/tangible`
 * `byBudget`, so the table costs no call of its own beyond the month rows.
 * It sits inside the Month view card (#2207) under its own heading: the
 * right column from lg up, after Money in and out below that.
 *
 * From md up a table with four columns and a 4 px red / green bar under each
 * category name, its length the square root of the category's size against
 * the largest (`barLengthOf`). "Includes: <names>" opens after a 1 second
 * hover anywhere in a category row's Budgets over cell; the Total row's pill
 * keeps its own note. Below md one mini card per row: name and count with an
 * (i) that opens the Includes note on tap, Spent and Still available side by
 * side under small captions, the bar full width.
 *
 * Every label-and-value pair explains itself on hover, focus and tap
 * (FigureNote), in words and never with an amount. Spent reads as a positive
 * amount in the neutral color on every row (#2207); Still available keeps
 * section 11 coloring (category rows neutral, the Total row as a total,
 * negatives red).
 *
 * @param {object} props
 * @param {Array} props.budgets                  active budgets, any type
 * @param {Array} props.budgetCategories
 * @param {Array|null} props.monthRows           `/budget-months` budgets, null while loading
 * @param {boolean} props.error                  the month rows failed to load
 * @param {Array} props.byBudget                 `/tangible` byBudget rows
 * @param {string} props.yearMonth
 * @param {Object} props.strings                 engine strings
 * @param {string} props.locale
 */
const BudgetsByCategory = ({
  budgets, budgetCategories, monthRows, error = false, byBudget, yearMonth, strings, locale,
}) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const s = (key) => strings[`dashboardN.table.${key}`];
  const title = interpolate(s('title'), { month: shortMonthYear(yearMonth, locale) });

  const dim = { fontSize: `${ds.font.dim}px`, lineHeight: 1.4, color: palette.text.dim };
  const dash = <Box component="span" sx={{ color: palette.text.dim }}>-</Box>;

  const amount = (cents, tone, color) => (
    <MaskedCurrency
      value={cents / 100}
      style={{ color: color || resolveAmountColor(palette, tone, cents < 0, cents === 0), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: tone === 'total' ? ds.font.weightMedium : ds.font.weightRegular }}
    />
  );
  // Spent reads as a positive amount (money out is stored negative), neutral
  // on every row, zero dim (#2207).
  const spentAmount = (row, tone) => {
    const cents = -(row.spentCents || 0);
    return amount(cents, tone, resolveAmountColor(palette, 'neutral', cents < 0, cents === 0));
  };

  // Hover text per cell; the pair is the FigureNote target.
  const spentNote = (row) => (row.kind === 'total' ? s('total.note') : interpolate(s('row.spent'), { count: row.budgetCount }));
  const availableNote = (row) => {
    if (row.kind === 'total') return s('total.note');
    const base = interpolate(s('row.available'), { count: row.budgetCount });
    return row.overCount > 0 ? `${base} ${interpolate(s('row.availableOver'), { names: joinNames(row.overNames) })}` : base;
  };
  const overNote = (row) => interpolate(s('row.over'), { names: joinNames(row.overNames) });
  const includesNote = (row) => interpolate(s('includes'), { names: joinNames(row.includes) });
  const barNote = (share) => (share >= 1
    ? s('barAllSpent')
    : interpolate(s('bar'), { spent: Math.round(share * 100), available: 100 - Math.round(share * 100) }));

  const pair = (note, children, sx = {}) => (
    <FigureNote note={note} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: `${ds.space[2]}px`, minWidth: 0, ...sx }}>
      {children}
    </FigureNote>
  );

  // Category names are links (section 11 Link: interactive, underline on
  // hover); the Total row stays text. On a phone a category card carries an
  // (i) after its count that opens the Includes note on tap (#2207).
  const nameSx = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const linkSx = {
    ...nameSx, border: 0, background: 'none', padding: 0, font: 'inherit', color: palette.interactive, cursor: 'pointer', textAlign: 'left',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  };
  const nameCell = (row, sx = {}) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: `${ds.space[2]}px`, minWidth: 0, color: palette.text.primary, fontWeight: row.kind === 'total' ? ds.font.weightMedium : ds.font.weightRegular, ...sx }}>
      {row.kind === 'category'
        ? <Box component="button" type="button" data-testid="category-link" onClick={() => scrollToBudgetGroup(row.key)} sx={linkSx}>{row.name}</Box>
        : <Box component="span" sx={nameSx}>{s(row.kind)}</Box>}
      {row.kind === 'category' && (
        <Box component="span" data-testid="category-count" sx={{ fontSize: '13px', color: palette.text.dim, whiteSpace: 'nowrap' }}>{`· ${row.budgetCount}`}</Box>
      )}
      {row.kind === 'category' && !isWide && (
        <FigureNote
          note={includesNote(row)}
          data-testid="category-includes-info"
          sx={{ alignSelf: 'center', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', flexShrink: 0, color: palette.text.dim }}
        >
          <IconInfoCircle size={16} aria-hidden />
        </FigureNote>
      )}
    </Box>
  );

  const overPill = (row) => (
    <Box
      component="span"
      data-testid="budgets-over"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', height: '24px', padding: '0 10px', borderRadius: '12px',
        fontSize: '12px', fontWeight: ds.font.weightMedium, backgroundColor: palette.tint.negative, color: palette.error.main, justifyContent: 'center',
      }}
    >
      {interpolate(s('overCount'), { count: row.overCount })}
      <Box component="span" aria-hidden>·</Box>
      {amount(row.overCents, 'row')}
    </Box>
  );
  // A category row's whole Budgets over cell opens its Includes note after a
  // 1 second hover; the Total row's pill keeps its own note.
  const overCell = (row) => {
    if (row.kind === 'total') {
      return <Box sx={{ textAlign: 'right' }}>{row.overCount ? pair(overNote(row), overPill(row)) : dash}</Box>;
    }
    return (
      <FigureNote
        note={includesNote(row)}
        enterDelay={1000}
        data-testid="budgets-over-cell"
        sx={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
      >
        {row.overCount ? overPill(row) : dash}
      </FigureNote>
    );
  };

  const bar = (row) => (row.kind === 'category' && row.share !== null ? (
    <FigureNote note={barNote(row.share)} data-testid="category-bar" data-length={Math.round(row.length * 100)} sx={{ display: 'flex', gap: '2px', height: '4px', width: isWide ? `${row.length * 100}%` : '100%' }}>
      {row.share > 0 && <Box sx={{ flex: `${row.share} 0 0`, height: '4px', borderRadius: '2px', backgroundColor: palette.error.main }} />}
      {row.share < 1 && <Box sx={{ flex: `${1 - row.share} 0 0`, height: '4px', borderRadius: '2px', backgroundColor: palette.success.main }} />}
    </FigureNote>
  ) : null);

  let body;
  if (error) {
    body = <Typography sx={{ ...dim, fontSize: `${ds.font.row}px`, py: `${ds.space[2]}px` }}>{s('error')}</Typography>;
  } else if (!monthRows) {
    body = <Box sx={{ py: `${ds.space[5]}px` }}><BalanceSkeleton width="180px" /></Box>;
  } else {
    const summary = summarizeBudgetsByCategory(budgets, budgetCategories, monthRows, byBudget);
    const table = dashboardCategoryTable(summary, { default: strings['budgetCategories.default'] });
    const rows = [...table.rows, table.total];
    const headerSx = { fontSize: '11px', letterSpacing: ds.font.letterUpper, textTransform: 'uppercase', color: palette.text.dim };
    const rowBorder = `1px solid ${palette.divider}`;

    // The Total row sits at the small indent and reads as a total (a hairline
    // opens it); category rows sit deeper.
    const tableRow = (row) => {
      const total = row.kind === 'total';
      const tone = total ? 'total' : 'row';
      return (
        <Box
          key={row.key}
          data-testid={`category-row-${row.key}`}
          data-kind={row.kind}
          sx={{
            display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, rowGap: '5px', alignItems: 'center',
            py: `${ds.space[3]}px`, borderBottom: total ? 'none' : rowBorder, borderTop: total ? rowBorder : 'none',
            fontSize: `${total ? 13.5 : 13}px`,
          }}
        >
          {nameCell(row, { pl: `${total ? ds.space[1] : ds.space[5]}px` })}
          <Box sx={{ textAlign: 'right' }}>{pair(spentNote(row), spentAmount(row, tone))}</Box>
          <Box sx={{ textAlign: 'right' }}>{pair(availableNote(row), amount(row.availableCents, tone))}</Box>
          {overCell(row)}
          {row.kind === 'category' && <Box sx={{ gridColumn: '1', px: `${ds.space[5]}px` }}>{bar(row)}</Box>}
        </Box>
      );
    };

    // Phone cards (#2179): category cards sit one step in from the edge, the
    // Total at the edge, so the summary level reads at a glance; every card
    // shares one two-column grid, Spent flush left and Still available flush
    // right. Names truncate on one line; no Budgets over pill on phones
    // (owner: not needed, visually disruptive).
    const cardRow = (row) => {
      const total = row.kind === 'total';
      const tone = total ? 'total' : 'row';
      const caption = (text, align) => <Box sx={{ ...headerSx, mb: '2px', textAlign: align }}>{text}</Box>;
      return (
        <Box key={row.key} data-testid={`category-row-${row.key}`} data-kind={row.kind} sx={{ py: `${ds.space[4]}px`, pl: total ? 0 : `${ds.space[4]}px`, borderBottom: total ? 'none' : rowBorder, borderTop: total ? rowBorder : 'none', fontSize: `${total ? 13.5 : 13}px` }}>
          {nameCell(row, { mb: `${ds.space[2]}px` })}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: `${ds.space[5]}px`, alignItems: 'end' }}>
            <Box sx={{ minWidth: 0 }}>{caption(s('colSpent'), 'left')}{pair(spentNote(row), spentAmount(row, tone))}</Box>
            <Box sx={{ minWidth: 0, textAlign: 'right' }}>
              {caption(s('colAvailable'), 'right')}
              {pair(availableNote(row), amount(row.availableCents, tone), { justifyContent: 'flex-end' })}
            </Box>
          </Box>
          {row.kind === 'category' && row.share !== null && <Box sx={{ mt: `${ds.space[3]}px` }}>{bar(row)}</Box>}
        </Box>
      );
    };

    body = (
      <Box data-testid="budgets-by-category" data-layout={isWide ? 'table' : 'cards'}>
        {isWide && (
          <Box data-testid="category-header-row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, py: `${ds.space[3]}px`, borderBottom: rowBorder, ...headerSx }}>
            <Box sx={{ pl: `${ds.space[1]}px` }}>{pair(s('colCategory.note'), <span>{s('colCategory')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colSpent.note'), <span>{s('colSpent')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colAvailable.note'), <span>{s('colAvailable')}</span>)}</Box>
            <Box sx={{ textAlign: 'right' }}>{pair(s('colOver.note'), <span>{s('colOver')}</span>)}</Box>
          </Box>
        )}
        {rows.map((row) => (isWide ? tableRow(row) : cardRow(row)))}
      </Box>
    );
  }

  return (
    <Box component="section" aria-label={title} data-testid="budgets-by-category-card">
      <Typography
        component="h3"
        sx={{ m: 0, py: `${ds.space[3]}px`, fontSize: `${ds.font.meta}px`, fontWeight: ds.font.weightMedium, color: palette.text.secondary }}
      >
        {title}
      </Typography>
      {body}
    </Box>
  );
};

export default BudgetsByCategory;
